import { safeFunnelAttribution, safelyTrack, type FunnelAttribution } from './funnel-analytics';

// TypeScript types for Penzu session structure
interface ClientApplication {
  id: number;
  key: string;
  secret: string;
}

interface ProMembership {
  id: number;
  package_name: string;
  renewal_date: string;
  renewal_date_i: number; // Unix timestamp in seconds (not milliseconds)
  subscription_length: number;
}

export interface SessionUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  has_mobile: boolean;
  is_pro: boolean;
  is_pro_plus: boolean;
  pro_membership?: ProMembership;
}

interface AccessToken {
  id: number;
  client_application: ClientApplication;
  expires_at: number; // Unix timestamp in seconds (not milliseconds)
  secret: string;
  token: string;
  user: SessionUser;
  user_id: number;
  created_at: string;
}

interface PenzuSession {
  access_token: AccessToken;
  expired_pro: boolean;
}

// Authentication API result types (shared between login and signup)
export interface AuthResultProMembership {
  id: number;
  renewal_date: Date;
  subscription_length: number;
  package_name: string;
  renewal_date_i: number;
}

export interface AuthResultUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_pro: boolean;
  is_pro_plus: boolean;
  has_mobile: boolean;
  pro_membership: AuthResultProMembership | null;
}

export interface AuthResultClientApplication {
  id: number;
  key: string;
  secret: string;
}

export interface AuthResultSuccess {
  purchase?: unknown;
  billing_contract?: unknown;
  access_token: {
    id: string | number;
    token: string;
    secret: string;
    user_id: string | number;
    expires_at: number;
    expired_pro: boolean;
    client_application: AuthResultClientApplication;
    user: AuthResultUser;
    created_at?: Date | string;
  };
}

export interface AuthResultError {
  error: import('./pricing/types').PricingErrorBody;
}

export type AuthResult = AuthResultSuccess | AuthResultError;

// Helper function to get and parse the session from localStorage
function getSession(): PenzuSession | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const sessionString = window.localStorage.getItem('pz-session');
    if (!sessionString) return null;
    return JSON.parse(sessionString) as PenzuSession;
  } catch {
    return null;
  }
}

/**
 * Helper function to check if a Unix timestamp (in seconds) has not expired.
 * @param timestampInSeconds - Unix timestamp in seconds (not milliseconds)
 * @returns true if the timestamp is in the future
 */
function isNotExpired(timestampInSeconds: number): boolean {
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return timestampInSeconds > nowInSeconds;
}

// Does not indicate that the user is currently fully authed, but that they have authed
// successfully previously and have an access token stored in their browser.
export function hasAuthenticated(): boolean {
  const session = getSession();
  return session !== null;
}

/**
 * Determines if the user has an active (not expired) session.
 */
export function isAuthenticated(): boolean {
  const session = getSession();
  if (!session) {
    return false;
  }

  return isNotExpired(session.access_token.expires_at);
}

/**
 * Returns the currently authenticated user or null if not authenticated.
 */
export function authenticatedUser(): SessionUser | null {
  if (!isAuthenticated()) {
    return null;
  }

  const session = getSession();
  if (!session) {
    return null;
  }

  return {
    id: session.access_token.user.id,
    email: session.access_token.user.email,
    first_name: session.access_token.user.first_name,
    last_name: session.access_token.user.last_name,
    has_mobile: session.access_token.user.has_mobile,
    is_pro: session.access_token.user.is_pro,
    is_pro_plus: session.access_token.user.is_pro_plus,
    pro_membership: session.access_token.user.pro_membership,
  };
}

/**
 * Returns true if the user is authenticated and has an active (not expired)
 * Penzu Pro or Penzu Pro Plus subscription.
 */
export function isPaidSubscriber(): boolean {
  if (!isAuthenticated()) {
    return false;
  }

  const session = getSession();
  if (!session) {
    return false;
  }

  const user = session.access_token.user;

  // Check if user has Pro or Pro Plus
  if (!user.is_pro && !user.is_pro_plus) {
    return false;
  }

  // Check if subscription hasn't expired
  if (user.pro_membership) {
    return isNotExpired(user.pro_membership.renewal_date_i);
  }

  return false;
}

/**
 * Returns true if the user is authenticated and has an active (not expired)
 * Penzu Pro subscription.
 */
export function isPenzuPro(): boolean {
  if (!isAuthenticated()) {
    return false;
  }

  const session = getSession();
  if (!session) {
    return false;
  }

  const user = session.access_token.user;

  // Check if user is Pro
  if (!user.is_pro) {
    return false;
  }

  // Check if subscription hasn't expired
  if (user.pro_membership) {
    return isNotExpired(user.pro_membership.renewal_date_i);
  }

  return false;
}

/**
 * Returns true if the user is authenticated and has an active (not expired)
 * Penzu Pro Plus subscription.
 */
export function isPenzuProPlus(): boolean {
  if (!isAuthenticated()) {
    return false;
  }

  const session = getSession();
  if (!session) {
    return false;
  }

  const user = session.access_token.user;

  // Check if user is Pro Plus
  if (!user.is_pro_plus) {
    return false;
  }

  // Check if subscription hasn't expired
  if (user.pro_membership) {
    return isNotExpired(user.pro_membership.renewal_date_i);
  }

  return false;
}

/**
 * Stores the authentication session in localStorage and sets up user tracking.
 * This is used by both signup and login flows. Tracker failures are isolated from session storage.
 *
 * @param result - The successful authentication result from the API
 * @param tracker - The tracking object with setUser and trackEvent methods
 * @param eventName - The name of the tracking event to fire (e.g., 'Signup Complete', 'Login Complete')
 * @param additionalStorage - Optional key-value pairs to store in localStorage
 * @param attribution - Optional allowlisted opening exposure for the completion event
 */
export function storeAuthSession(
  result: AuthResultSuccess,
  tracker: {
    setUser: (userId: string | number) => void;
    trackEvent: (name: string, props?: Record<string, unknown>) => void;
  },
  eventName: string,
  additionalStorage?: Record<string, string>,
  attribution?: FunnelAttribution,
): void {
  try {
    const session = {
      ...result,
    };
    // Add created_at timestamp if not present
    if (!session.access_token.created_at) {
      const now = new Date();
      session.access_token.created_at = now.toISOString();
    }
    const sessionString = JSON.stringify(session);
    window.localStorage.setItem('pz-session', sessionString);
    // A newly persisted complete session resolves any uncertain pricing token rotation.
    try {
      window.sessionStorage.removeItem(`pz_pricing_refresh:${result.access_token.user.id}`);
    } catch {
      /* Optional rotation marker must not block ordinary sign-in. */
    }

    // Store any additional items
    if (additionalStorage) {
      Object.entries(additionalStorage).forEach(([key, value]) => {
        window.localStorage.setItem(key, value);
      });
    }

    safelyTrack(() => tracker.setUser(result.access_token.user_id));
    safelyTrack(() =>
      tracker.trackEvent(eventName, {
        state_name: 'index.new',
        ...safeFunnelAttribution(attribution),
      }),
    );
  } catch (error) {
    console.error('Unexpected error storing auth session:', error);
    throw error;
  }
}

// Tab-scoped flag marking that the user created an account during this visit.
const NEW_SIGNUP_FLAG = 'pz-new-signup';

/**
 * Marks that the user signed up during this browser session, so a subsequent
 * checkout can route them to /welcome after purchase. Best-effort: storage can
 * be unavailable (private mode, blocked/partitioned storage), and the routing
 * is a nice-to-have, so failures degrade silently to the default destination.
 */
export function markNewSignup(): void {
  try {
    window.sessionStorage.setItem(NEW_SIGNUP_FLAG, 'true');
  } catch {
    // Ignore — see note above.
  }
}

/**
 * Returns true if the user signed up earlier in this session, consuming the
 * flag so it applies to a single purchase only. Best-effort: returns false if
 * storage is unavailable rather than throwing into the post-purchase flow.
 */
export function consumeNewSignupFlag(): boolean {
  try {
    if (window.sessionStorage.getItem(NEW_SIGNUP_FLAG) === 'true') {
      window.sessionStorage.removeItem(NEW_SIGNUP_FLAG);
      return true;
    }
  } catch {
    // Ignore — treat unavailable storage as "no flag".
  }
  return false;
}
